import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/AppLayout"
import { Head, usePage } from "@inertiajs/react"
import {
  IconBook,
  IconBriefcase,
  IconChartLine,
  IconCheck,
  IconClipboardList,
  IconStar,
  IconUsers,
} from "@tabler/icons-react"

interface Props {
  [key: string]: unknown
  is_admin: boolean
}

const OBJECTIVES = [
  "Evaluar el desempe\u00f1o de cada colaborador en base a objetivos y KPIs establecidos.",
  "Brindar retroalimentaci\u00f3n constructiva y establecer planes de mejora.",
  "Identificar oportunidades de promoci\u00f3n y avance de seniority.",
  "Ajustar compensaciones y beneficios en base al rendimiento y logros.",
]

const STEPS = [
  {
    title: "Autoevaluaci\u00f3n",
    description: "Cada colaborador completa un formulario de autoevaluaci\u00f3n antes de la reuni\u00f3n.",
  },
  {
    title: "Evaluaci\u00f3n del Manager",
    description: "El manager directo eval\u00faa el desempe\u00f1o basado en KPI's y feedback recibido.",
  },
  {
    title: "Revisi\u00f3n 360\u00b0",
    description: "Se recopila feedback de pares y otros equipos.",
  },
  {
    title: "Reuni\u00f3n de Evaluaci\u00f3n",
    description:
      "Se lleva a cabo una reuni\u00f3n para discutir el desempe\u00f1o y establecer metas para el siguiente periodo.",
  },
  {
    title: "Decisiones sobre Seniority y Compensaciones",
    description: "En base a la evaluaci\u00f3n, se pueden proponer ascensos de nivel o ajustes salariales.",
  },
]

const CRITERIA = [
  "Cumplimiento de objetivos y KPIs",
  "Impacto en el equipo y organizaci\u00f3n",
  "Habilidades t\u00e9cnicas y de liderazgo",
  "Iniciativa y autonom\u00eda",
  "Colaboraci\u00f3n y comunicaci\u00f3n efectiva",
]

const GROWTH_ITEMS = [
  {
    icon: IconBriefcase,
    label: "Mayor responsabilidad en proyectos",
  },
  {
    icon: IconBook,
    label: "Participaci\u00f3n en iniciativas estrat\u00e9gicas",
  },
  {
    icon: IconUsers,
    label: "Oportunidad de liderar equipos o iniciativas",
  },
]

export default function PerformanceLifecycle() {
  const { is_admin } = usePage<Props>().props

  return (
    <>
      <Head title="Performance Lifecycle - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>Growth & Career</BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Performance Lifecycle</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Performance Lifecycle</h1>
          <span className="text-muted-foreground text-xs">Last Updated: Feb 14, 2025</span>
        </div>

        {/* Intro */}
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-lg">Performance Reviews - Evaluaciones de Desempe&ntilde;o</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Realizamos dos evaluaciones de desempe&ntilde;o al a&ntilde;o en los meses de{" "}
              <span className="text-primary font-semibold">abril</span> y{" "}
              <span className="text-primary font-semibold">octubre</span>. Estas revisiones tienen como objetivo
              proporcionar retroalimentaci&oacute;n estructurada sobre el rendimiento de cada colaborador y brindar
              oportunidades de crecimiento dentro de la empresa.
            </p>
          </CardContent>
        </Card>

        {/* Objectives */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
                <IconBook className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <CardTitle className="text-lg">Objetivos de las Performance Reviews</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {OBJECTIVES.map((obj, i) => (
                <li key={i} className="flex items-start gap-2">
                  <IconCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
                  <span className="text-muted-foreground text-sm">{obj}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Evaluation Process */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900">
                <IconClipboardList className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-lg">Proceso de Evaluaci&oacute;n</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ol className="relative border-l border-border ml-3">
              {STEPS.map((step, i) => (
                <li key={i} className={`${i < STEPS.length - 1 ? "mb-6" : ""} ml-6`}>
                  <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 ring-4 ring-background dark:bg-blue-900">
                    <span className="text-xs font-bold text-blue-800 dark:text-blue-300">{i + 1}</span>
                  </span>
                  <h3 className="mb-1 text-sm font-semibold">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">{step.description}</p>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {/* Evaluation Criteria */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-100 dark:bg-yellow-900">
                <IconStar className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <CardTitle className="text-lg">Criterios de Evaluaci&oacute;n</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {CRITERIA.map((criterion, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border p-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-yellow-50 dark:bg-yellow-900/30">
                    <IconCheck className="h-4 w-4 text-yellow-500" />
                  </div>
                  <span className="text-sm font-medium">{criterion}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Promotions and Growth */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900">
                <IconChartLine className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <CardTitle className="text-lg">Ascensos y Crecimiento</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4 text-sm">
              Los colaboradores que demuestren un alto desempe&ntilde;o pueden avanzar en su seniority dentro de la
              organizaci&oacute;n. Esto implica:
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {GROWTH_ITEMS.map((item, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-purple-200 bg-purple-50 p-4 text-center dark:border-purple-800 dark:bg-purple-900/20"
                >
                  <item.icon className="mx-auto mb-2 h-8 w-8 text-purple-600 dark:text-purple-400" />
                  <p className="text-sm font-medium text-purple-800 dark:text-purple-300">{item.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Links (Admin only) */}
        {is_admin && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Links</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" asChild>
                  <a href="/performance_review_cycles">
                    <IconClipboardList className="mr-2 h-4 w-4" />
                    Review Cycles
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="/admin/seniority_levels">
                    <IconChartLine className="mr-2 h-4 w-4" />
                    Compensations
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

PerformanceLifecycle.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
